import { useState } from "react"
import Step1BillingForm from "./Step1BillingForm"
import Step2PaymentForm from "./Step2PaymentForm"
import Step3Result from "./Step3Result"
import { upgradeSubscription } from "@/api/kuizy"
import { Check } from "lucide-react"

const EMPTY_BILLING = {
  organization_name: "",
  tax_id: "",
  contact_name: "",
  email: "",
  phone: "",
  addr1: "",
  addr2: "",
  sub_district: "",
  district: "",
  province: "",
  postal_code: "",
}

const EMPTY_PAYMENT = { method: "qr" }

const STEPS = [
  { id: 1, label: "ที่อยู่วางบิล / ใบเสร็จ" },
  { id: 2, label: "ชำระเงิน" },
  { id: 3, label: "เสร็จสิ้น" },
]

function StepBar({ current }) {
  return (
    <div className="flex items-center w-full mb-8">
      {STEPS.map((s, i) => {
        const done = current > s.id
        const active = current === s.id
        return (
          <div key={s.id} className="flex items-center flex-1 last:flex-none">
            {/* Circle */}
            <div className={`flex items-center gap-2 shrink-0`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition
                ${done ? "bg-pink-500 border-pink-500 text-white" : active ? "border-pink-500 text-pink-500 bg-white" : "border-gray-300 text-gray-400 bg-white"}`}>
                {done ? (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : s.id}
              </div>
              <span className={`text-xs font-medium whitespace-nowrap hidden sm:inline
                ${done || active ? "text-gray-700" : "text-gray-400"}`}>
                {s.label}
              </span>
            </div>
            {/* Connector */}
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-0.5 mx-3 rounded-full transition-all"
                style={{ backgroundColor: done ? "#ec4899" : "#e5e7eb" }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function CheckoutFlow({ plan, onClose, onSuccess }) {
  const [step, setStep] = useState(1)
  const [billing, setBilling] = useState(EMPTY_BILLING)
  const [payment, setPayment] = useState(EMPTY_PAYMENT)
  const [result, setResult] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const vat = Math.round((plan.price_monthly || 0) * 0.07)
  const total = (plan.price_monthly || 0) + vat

  async function handleConfirmPayment() {
    setSubmitting(true)
    try {
      await upgradeSubscription({
        plan_id: plan.id,
        billing_cycle: "month",
        amount_paid: total,
        billing_info: billing,
        payment_method: payment.method,
      })
      setResult({ success: true })
      setStep(3)
      onSuccess?.()
    } catch (err) {
      setResult({ success: false, message: err?.response?.data?.error || "เกิดข้อผิดพลาดในการชำระเงิน" })
      setStep(3)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="py-2">
      {/* Plan info banner */}


      <StepBar current={step} />

      <div className="inline-flex items-center gap-2 mb-6 bg-pink-50 border border-pink-200 rounded-full px-2 py-2 text-sm">
        <Check className="w-4 h-4 text-pink-600" />
        <span className="text-pink-700">
          {plan.display_name} — <strong>฿{total.toLocaleString()}</strong> / เดือน (รวม VAT)
        </span>
      </div>

      {step === 1 && (
        <Step1BillingForm
          plan={plan}
          billing={billing}
          onChange={setBilling}
          onNext={() => setStep(2)}
          onBack={onClose}
        />
      )}

      {step === 2 && (
        <Step2PaymentForm
          plan={plan}
          billing={billing}
          payment={payment}
          onChange={setPayment}
          onNext={handleConfirmPayment}
          onBack={() => setStep(1)}
        />
      )}

      {step === 3 && (
        <Step3Result
          plan={plan}
          billing={billing}
          payment={payment}
          result={result}
          onBackToSubscription={onClose}
        />
      )}

      {/* Bottom nav (visible on step 1 & 2 only) */}
      {step < 3 && (
        <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={step === 1 ? onClose : () => setStep(s => s - 1)}
            className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-5 py-3 sm:py-2.5 rounded-xl border border-gray-300 text-sm font-medium hover:bg-gray-50 transition order-1 sm:order-none"
          >
            ← ย้อนกลับ
          </button>

          {step === 1 && (
            <button
              onClick={() => setStep(2)}
              disabled={!(billing.organization_name && billing.tax_id && billing.contact_name && billing.email && billing.phone && billing.addr1 && billing.sub_district && billing.district && billing.province && billing.postal_code)}
              className="w-full sm:flex-1 sm:ml-4 py-3 rounded-xl bg-pink-500 hover:bg-pink-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition order-2 sm:order-none"
            >
              ถัดไป: ชำระเงิน →
            </button>
          )}

          {step === 2 && (
            <button
              onClick={handleConfirmPayment}
              disabled={submitting || (payment.method === "card" && !(payment.card_name && payment.card_number && payment.exp_month && payment.exp_year?.length === 4 && payment.cvv))}
              className="w-full justify-center sm:flex-1 sm:ml-4 py-3 rounded-xl bg-pink-500 hover:bg-pink-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition flex items-center gap-2 order-2 sm:order-none"
            >
              {submitting && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              )}
              ยืนยันการชำระเงิน ฿{total.toLocaleString()} →
            </button>
          )}
        </div>
      )}
    </div>
  )
}
